import { useMemo } from "react";

import dayjs from "dayjs";

import { useTranslate } from "@similarweb/i18n-provider";
import { Image as ImageIcon, Month } from "@similarweb/icons";
import { shortMonthAndDay, fullMonthDayAndYear } from "@similarweb/pro-logic-components";
import { colorsPalettes } from "@similarweb/styles";
import { Card, Tooltip } from "@similarweb/ui-components";
import { Flex, Stack, Text, TruncatedText } from "@similarweb/ui-components/foundations";

import { ImageWithFallback } from "../../common/image-fallback/ImageWithFallback";
import { InfoItem } from "../../common/InfoItem";
import { OpenInNewTab } from "../../common/OpenInNewTab";
import { BorderCard, FooterWrapper, ImageContainer } from "../../common/styledComponents";

export const shoppingAdCardSize = {
    width: 360,
    height: 300,
};

interface IShoppingAdCardProps {
    firstSeen: string;
    imageUrl: string;
    landingPage: string;
    price: string;
    title: string;
}

export const ShoppingAdCard = ({ price, title, imageUrl, firstSeen, landingPage }: IShoppingAdCardProps) => {
    const { translate } = useTranslate();

    const { shortDate, fullDate } = useMemo(() => {
        const date = dayjs(firstSeen);

        return {
            shortDate: date.format(shortMonthAndDay),
            fullDate: date.format(fullMonthDayAndYear),
        };
    }, [firstSeen]);

    return (
        <BorderCard
            data-automation="shopping-ad-card"
            style={{ width: shoppingAdCardSize.width, height: shoppingAdCardSize.height }}
        >
            <Card.Header
                flexGrow={1}
                alignItems="flex-start"
            >
                <Stack
                    gap="12px"
                    paddingY="16px"
                    width="100%"
                >
                    <Text
                        fontVariant="subtitle2"
                        color={colorsPalettes.carbon[500]}
                    >
                        {price}
                    </Text>
                    <TruncatedText
                        lineClamp={2}
                        fontVariant="body2"
                        color={colorsPalettes.carbon[400]}
                    >
                        {title}
                    </TruncatedText>
                </Stack>
            </Card.Header>
            <ImageContainer>
                <ImageWithFallback
                    src={imageUrl}
                    alt={title}
                />
            </ImageContainer>
            <FooterWrapper data-automation="ad-card-footer">
                <Flex gap="12px">
                    <InfoItem
                        icon={ImageIcon}
                        title="Image"
                        tooltip={translate("activation.alerts.cards.social-post.creative-type", { type: "Image" })}
                    />
                    <InfoItem
                        icon={Month}
                        title={shortDate}
                        tooltip={translate("activation.alerts.cards.social-post.first-seen", {
                            date: fullDate,
                        })}
                    />
                </Flex>
                <Tooltip content={translate("activation.alerts.cards.ads.landing-page")}>
                    <OpenInNewTab
                        url={landingPage}
                        data-automation="open-landing-page-button"
                    />
                </Tooltip>
            </FooterWrapper>
        </BorderCard>
    );
};
